import { getEnv } from '@/config/env';
import { ApiError, type ApiErrorBody } from './errors';

export type RequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  tenantId?: string;
  signal?: AbortSignal;
};

function getBaseUrl(): string {
  const env = getEnv();
  return env.VITE_API_BASE_URL || '';
}

function getAuthHeaders(tenantId?: string): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    // Skip ngrok free-tier browser interstitial (otherwise CORS fails with a fake 200 HTML page)
    'ngrok-skip-browser-warning': 'true',
  };

  const token = localStorage.getItem('voicera:session:token');
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const tid = tenantId ?? localStorage.getItem('voicera:tenant:id');
  if (tid) {
    headers['X-Tenant-Id'] = tid;
  }

  return headers;
}

/** Typed fetch wrapper — all API modules use this client. */
export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, headers = {}, tenantId, signal } = options;
  const url = `${getBaseUrl()}${path}`;

  const response = await fetch(url, {
    method,
    headers: { ...getAuthHeaders(tenantId), ...headers },
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  });

  if (response.status === 204) {
    return undefined as T;
  }

  const data = (await response.json().catch(() => ({}))) as T & ApiErrorBody;

  if (!response.ok) {
    throw new ApiError(data.message ?? `Request failed (${response.status})`, {
      code: data.code ?? `HTTP_${response.status}`,
      status: response.status,
      fieldErrors: data.fieldErrors,
    });
  }

  return data;
}

/** Health check — verifies API reachability. */
export async function pingApi(): Promise<{ ok: boolean }> {
  return apiRequest('/api/v1/health');
}
